/**
 * Interactive Strategy Canvas configuration for each template.
 *
 * Contains guided questions, challenge rules, story prompt templates,
 * and relatedSections mappings. Import and merge with the base
 * CanvasTemplateDef to get the full interactive config.
 */

import type { GuidedQuestion, ChallengeRule } from './templates.js'

// ---------------------------------------------------------------------------
// Business Model Canvas — Guided Questions
// ---------------------------------------------------------------------------

export const BMC_GUIDED_QUESTIONS: GuidedQuestion[] = [
  {
    sectionId: 'customer-segments',
    question: 'Who are the most important customers you serve?',
    hint: 'Think about distinct groups with different needs, behaviours, or willingness to pay. Consider mass market, niche, segmented, or multi-sided platforms.',
    fallbackSuggestions: ['Enterprise buyers', 'SMB owners', 'End consumers', 'Platform users'],
    order: 1,
  },
  {
    sectionId: 'value-propositions',
    question: 'What value do you deliver to {ref:customer-segments}?',
    hint: 'What problem do you solve or need do you satisfy? Consider newness, performance, customisation, design, price, cost reduction, risk reduction, accessibility, or convenience.',
    fallbackSuggestions: ['Time savings', 'Cost reduction', 'Better experience', 'Risk mitigation'],
    order: 2,
  },
  {
    sectionId: 'channels',
    question: 'How do {ref:customer-segments} find out about and buy your product?',
    hint: 'Consider the full journey: awareness, evaluation, purchase, delivery, and after-sales. Different segments often need different channels.',
    fallbackSuggestions: ['Direct sales team', 'Partner/reseller network', 'Online self-service', 'Industry events'],
    order: 3,
  },
  {
    sectionId: 'customer-relationships',
    question: 'What kind of relationship does each of {ref:customer-segments} expect?',
    hint: 'Think about self-service, personal assistance, automated services, communities, or co-creation. The right relationship drives retention.',
    fallbackSuggestions: ['Self-service platform', 'Dedicated account management', 'Automated onboarding', 'Community & forums'],
    order: 4,
  },
  {
    sectionId: 'key-activities',
    question: 'What must you do exceptionally well to deliver {ref:value-propositions}?',
    hint: 'Consider production, problem-solving, platform management, supply chain, and any activities that are core to your model.',
    fallbackSuggestions: ['Product development', 'Platform operations', 'Partner management', 'Customer acquisition'],
    order: 5,
  },
  {
    sectionId: 'key-resources',
    question: 'What key resources does your value proposition require?',
    hint: 'Think physical, intellectual (patents, data), human, and financial resources. What assets are indispensable?',
    fallbackSuggestions: ['Engineering team', 'Proprietary technology', 'Brand reputation', 'Customer data'],
    order: 6,
  },
  {
    sectionId: 'key-partners',
    question: 'Who are the partners you cannot succeed without?',
    hint: 'Think about suppliers, technology providers, distribution partners, and strategic alliances essential to your model.',
    fallbackSuggestions: ['Technology platform provider', 'Distribution partner', 'Manufacturing partner', 'Strategic alliance'],
    order: 7,
  },
  {
    sectionId: 'cost-structure',
    question: 'What are your biggest costs to operate this model?',
    hint: 'Consider fixed vs variable costs, economies of scale, and which costs are driven by your key resources and activities.',
    fallbackSuggestions: ['Engineering & R&D headcount', 'Infrastructure & hosting', 'Sales & marketing', 'Operations & support'],
    order: 8,
  },
  {
    sectionId: 'revenue-streams',
    question: 'How does the business make money from {ref:value-propositions}?',
    hint: 'Think about pricing models: subscriptions, one-time sales, licensing, freemium, usage-based, marketplace fees, or advertising.',
    fallbackSuggestions: ['SaaS subscription', 'One-time license', 'Usage-based pricing', 'Professional services'],
    order: 9,
  },
]

// ---------------------------------------------------------------------------
// Business Model Canvas — Challenge Rules
// ---------------------------------------------------------------------------

export const BMC_CHALLENGE_RULES: ChallengeRule[] = [
  {
    id: 'empty-revenue',
    type: 'empty_section',
    severity: 'critical',
    title: 'Revenue model undefined',
    descriptionTemplate: "You've defined value propositions ({ref:value-propositions}) but haven't specified how you make money. Without revenue streams, the business model is incomplete.",
    affectedSection: 'revenue-streams',
  },
  {
    id: 'channel-blindspot',
    type: 'empty_section',
    severity: 'critical',
    title: 'Channel blindspot',
    descriptionTemplate: 'How do customers actually discover and buy your product? {ref:customer-segments} have complex buying journeys that need deliberate channel strategy.',
    affectedSection: 'channels',
  },
  {
    id: 'resource-gap',
    type: 'imbalance',
    severity: 'critical',
    title: 'Resource gap',
    descriptionTemplate: 'You have {count:customer-segments} customer segments and {count:value-propositions} value propositions but only {count:key-resources} key resources. Do you have enough to deliver at this scale?',
    affectedSection: 'key-resources',
  },
  {
    id: 'segment-spread',
    type: 'strategic_focus',
    severity: 'strategic',
    title: 'Segment spread risk',
    descriptionTemplate: "You're targeting {count:customer-segments} customer segments. Each has different needs, buying patterns, and channels. Can you serve them all well with your current resources?",
    affectedSection: 'customer-segments',
  },
  {
    id: 'relationship-vacuum',
    type: 'empty_section',
    severity: 'strategic',
    title: 'Relationship vacuum',
    descriptionTemplate: 'Your value propositions sound product-focused. But competitive moats are rarely built on features alone. How do you build lasting relationships with {ref:customer-segments}?',
    affectedSection: 'customer-relationships',
  },
]

// ---------------------------------------------------------------------------
// Business Model Canvas — Related Sections (for connections view)
// ---------------------------------------------------------------------------

/** Related sections with descriptive relationship labels for the connections view. */
export const BMC_RELATED_SECTIONS: Record<string, { target: string; label: string }[]> = {
  'key-partners': [
    { target: 'key-activities', label: 'enables' },
    { target: 'key-resources', label: 'provides' },
  ],
  'key-activities': [
    { target: 'value-propositions', label: 'creates' },
    { target: 'key-resources', label: 'requires' },
  ],
  'key-resources': [
    { target: 'key-activities', label: 'enables' },
    { target: 'value-propositions', label: 'supports' },
  ],
  'value-propositions': [
    { target: 'customer-segments', label: 'offered to' },
    { target: 'customer-relationships', label: 'strengthened by' },
  ],
  'customer-relationships': [
    { target: 'customer-segments', label: 'maintains' },
    { target: 'channels', label: 'delivered through' },
  ],
  'channels': [
    { target: 'customer-segments', label: 'reaches' },
    { target: 'value-propositions', label: 'delivers' },
  ],
  'customer-segments': [
    { target: 'value-propositions', label: 'receives' },
    { target: 'channels', label: 'reached via' },
    { target: 'customer-relationships', label: 'expects' },
  ],
  'cost-structure': [
    { target: 'key-resources', label: 'funds' },
    { target: 'key-activities', label: 'funds' },
    { target: 'key-partners', label: 'funds' },
  ],
  'revenue-streams': [
    { target: 'value-propositions', label: 'monetizes' },
    { target: 'customer-segments', label: 'paid by' },
  ],
}

// ---------------------------------------------------------------------------
// Business Model Canvas — Story Prompt Template
// ---------------------------------------------------------------------------

export const BMC_STORY_PROMPT = `You are a product strategist. Write a compelling business model narrative for {projectName}.

Canvas data:
{canvasData}

Rules:
- Write in present tense, active voice
- Start with what they do and why it matters
- Then explain who they serve and what makes those segments attractive
- Then describe the strategic logic: how resources, activities, and partnerships come together
- End with the revenue logic
- No bullet points. No headers. No markdown.
- Maximum 200 words
- If sections are empty, don't mention them - focus on what's known
- Be specific, not generic. Use the actual entity names.`

// ---------------------------------------------------------------------------
// Value Proposition Canvas — Guided Questions
// ---------------------------------------------------------------------------

export const VPC_GUIDED_QUESTIONS: GuidedQuestion[] = [
  {
    sectionId: 'customer-jobs',
    question: 'What functional, social, or emotional jobs are your customers trying to get done?',
    hint: 'Think about tasks they perform, problems they solve, or needs they satisfy. Include functional (get something done), social (look good, gain status), and emotional (feel secure, happy) jobs.',
    fallbackSuggestions: ['Complete a task faster', 'Reduce manual effort', 'Look professional to peers', 'Feel confident in decisions'],
    order: 1,
  },
  {
    sectionId: 'customer-gains',
    question: 'What outcomes and benefits do your customers desire when doing {ref:customer-jobs}?',
    hint: 'Think about required gains (expected), expected gains (hoped for), desired gains (beyond expectations), and unexpected gains (delightful surprises).',
    fallbackSuggestions: ['Save time', 'Higher quality output', 'Better collaboration', 'Peace of mind'],
    order: 2,
  },
  {
    sectionId: 'customer-pains',
    question: 'What frustrations or risks do customers face when performing {ref:customer-jobs}?',
    hint: 'Consider undesired outcomes, obstacles, and risks. What makes their current experience bad? What do they fear or worry about?',
    fallbackSuggestions: ['Too slow or tedious', 'Error-prone process', 'Expensive to maintain', 'Lack of visibility'],
    order: 3,
  },
  {
    sectionId: 'value-propositions',
    question: 'What is your core value proposition that addresses {ref:customer-jobs}?',
    hint: 'Describe the overall promise you make to customers. How do you help them get their jobs done better, faster, or cheaper?',
    fallbackSuggestions: ['Automated workflow', 'All-in-one platform', 'Expert guidance', 'Real-time insights'],
    order: 4,
  },
  {
    sectionId: 'products-services',
    question: 'What specific products or services deliver {ref:value-propositions}?',
    hint: 'List the concrete offerings — features, modules, services, support tiers — that your value proposition is built around.',
    fallbackSuggestions: ['Core platform', 'Analytics dashboard', 'API integration', 'Professional services'],
    order: 5,
  },
  {
    sectionId: 'pain-relievers',
    question: 'How do {ref:products-services} specifically eliminate or reduce {ref:customer-pains}?',
    hint: 'Be precise about which pains you relieve and how. The best pain relievers address the most extreme customer pains.',
    fallbackSuggestions: ['Eliminates manual steps', 'Prevents common errors', 'Reduces cost by 50%', 'Provides real-time alerts'],
    order: 6,
  },
]

// ---------------------------------------------------------------------------
// Value Proposition Canvas — Challenge Rules
// ---------------------------------------------------------------------------

export const VPC_CHALLENGE_RULES: ChallengeRule[] = [
  {
    id: 'vpc-no-jobs',
    type: 'empty_section',
    severity: 'critical',
    title: 'Customer jobs undefined',
    descriptionTemplate: "You haven't defined any customer jobs. Without understanding what customers are trying to do, your value proposition has no foundation.",
    affectedSection: 'customer-jobs',
  },
  {
    id: 'vpc-no-pains',
    type: 'empty_section',
    severity: 'critical',
    title: 'No customer pains identified',
    descriptionTemplate: "You've defined {ref:customer-jobs} but haven't mapped their pains. Understanding frustrations is essential for building pain relievers.",
    affectedSection: 'customer-pains',
  },
  {
    id: 'vpc-no-relievers',
    type: 'empty_section',
    severity: 'critical',
    title: 'No pain relievers',
    descriptionTemplate: "You have {count:customer-pains} customer pains but no pain relievers. How does your product address what frustrates customers?",
    affectedSection: 'pain-relievers',
  },
  {
    id: 'vpc-fit-gap',
    type: 'imbalance',
    severity: 'strategic',
    title: 'Product-market fit gap',
    descriptionTemplate: "You have {count:customer-pains} pains but only {count:pain-relievers} pain relievers. Are there unaddressed pains that represent opportunity?",
    affectedSection: 'pain-relievers',
  },
  {
    id: 'vpc-feature-first',
    type: 'strategic_focus',
    severity: 'strategic',
    title: 'Feature-first thinking',
    descriptionTemplate: "You have {count:products-services} products/services but only {count:customer-jobs} customer jobs. Are you building features without clear customer needs?",
    affectedSection: 'products-services',
  },
]

// ---------------------------------------------------------------------------
// Value Proposition Canvas — Story Prompt
// ---------------------------------------------------------------------------

export const VPC_STORY_PROMPT = `You are a product strategist. Write a compelling product-market fit narrative for {projectName}.

Canvas data:
{canvasData}

Rules:
- Write in present tense, active voice
- Start with what customers are trying to accomplish (their jobs)
- Then describe their frustrations and unmet needs
- Then explain how the product specifically addresses those pains
- End with the value proposition — the promise that ties it all together
- No bullet points. No headers. No markdown.
- Maximum 200 words
- If sections are empty, don't mention them - focus on what's known
- Be specific, not generic. Use the actual entity names.`

// ---------------------------------------------------------------------------
// Positioning Canvas — Guided Questions
// ---------------------------------------------------------------------------

export const POS_GUIDED_QUESTIONS: GuidedQuestion[] = [
  {
    sectionId: 'product',
    question: 'What product or service are you positioning?',
    hint: 'State the product name and a one-line description. Be specific — positioning is about one offering, not your whole company.',
    fallbackSuggestions: ['SaaS platform', 'Mobile app', 'Professional service', 'Hardware device'],
    order: 1,
  },
  {
    sectionId: 'competitive-alternatives',
    question: 'What would customers use if {ref:product} didn\'t exist?',
    hint: 'Think beyond direct competitors. Include spreadsheets, manual processes, hiring someone, or doing nothing. These are the real alternatives.',
    fallbackSuggestions: ['Spreadsheets & manual work', 'Incumbent enterprise tool', 'Hiring a specialist', 'Status quo (doing nothing)'],
    order: 2,
  },
  {
    sectionId: 'unique-attributes',
    question: 'What does {ref:product} have that {ref:competitive-alternatives} don\'t?',
    hint: 'List only capabilities that are truly unique to your product. If a competitor also has it, it\'s not a differentiator.',
    fallbackSuggestions: ['AI-powered automation', 'Real-time collaboration', 'No-code configuration', 'Industry-specific workflows'],
    order: 3,
  },
  {
    sectionId: 'value',
    question: 'What value do {ref:unique-attributes} enable for customers?',
    hint: 'Translate features into customer outcomes. Don\'t say "AI-powered" — say "reduces analysis time from hours to minutes."',
    fallbackSuggestions: ['Faster time-to-value', 'Lower total cost', 'Better decision quality', 'Reduced operational risk'],
    order: 4,
  },
  {
    sectionId: 'who-cares',
    question: 'Who cares a lot about {ref:value}?',
    hint: 'Describe the characteristics of buyers who value your differentiation most. These are your best-fit customers — not the entire market.',
    fallbackSuggestions: ['Fast-growing startups', 'Enterprise digital teams', 'Resource-constrained SMBs', 'Innovation-focused leaders'],
    order: 5,
  },
  {
    sectionId: 'market-category',
    question: 'What market category best frames {ref:value} for {ref:who-cares}?',
    hint: 'The category sets expectations. Pick one that highlights your strengths. You can create a new subcategory if existing ones don\'t fit.',
    fallbackSuggestions: ['Existing market leader', 'New subcategory', 'Adjacent market entry', 'Category creation'],
    order: 6,
  },
]

// ---------------------------------------------------------------------------
// Positioning Canvas — Challenge Rules
// ---------------------------------------------------------------------------

export const POS_CHALLENGE_RULES: ChallengeRule[] = [
  {
    id: 'pos-no-alternatives',
    type: 'empty_section',
    severity: 'critical',
    title: 'No competitive alternatives',
    descriptionTemplate: "Without understanding what customers do today, you can't explain why {ref:product} is better. Positioning starts with alternatives.",
    affectedSection: 'competitive-alternatives',
  },
  {
    id: 'pos-no-differentiators',
    type: 'empty_section',
    severity: 'critical',
    title: 'No unique attributes',
    descriptionTemplate: "You've identified {ref:competitive-alternatives} but haven't defined what makes {ref:product} different. Without differentiators, you're a commodity.",
    affectedSection: 'unique-attributes',
  },
  {
    id: 'pos-no-audience',
    type: 'empty_section',
    severity: 'critical',
    title: 'Best-fit customer undefined',
    descriptionTemplate: "You haven't defined who cares most about your value. Trying to serve everyone means differentiating for no one.",
    affectedSection: 'who-cares',
  },
  {
    id: 'pos-feature-value-gap',
    type: 'imbalance',
    severity: 'strategic',
    title: 'Features without value translation',
    descriptionTemplate: "You have {count:unique-attributes} unique attributes but only {count:value} value statements. Customers buy value, not features.",
    affectedSection: 'value',
  },
]

// ---------------------------------------------------------------------------
// Positioning Canvas — Story Prompt
// ---------------------------------------------------------------------------

export const POS_STORY_PROMPT = `You are a positioning strategist (April Dunford style). Write a crisp positioning statement for {projectName}.

Canvas data:
{canvasData}

Rules:
- Write in present tense, active voice
- Start with who the best-fit customer is and what they struggle with today
- Then explain the competitive alternatives and their shortcomings
- Then introduce the product and its unique attributes
- End with the value these attributes enable and the market category that frames it
- No bullet points. No headers. No markdown.
- Maximum 200 words
- If sections are empty, don't mention them - focus on what's known
- Be specific, not generic. Use the actual entity names.`

// ---------------------------------------------------------------------------
// Problem Statement Canvas — Guided Questions
// ---------------------------------------------------------------------------

export const PSC_GUIDED_QUESTIONS: GuidedQuestion[] = [
  {
    sectionId: 'problem',
    question: 'What is the core problem you want to solve?',
    hint: 'State the problem clearly and specifically. Avoid solution language. Focus on the root cause, not symptoms.',
    fallbackSuggestions: ['Information is scattered', 'Process is too slow', 'Decisions lack data', 'Coordination breaks down'],
    order: 1,
  },
  {
    sectionId: 'context',
    question: 'When and where does {ref:problem} occur?',
    hint: 'Describe the specific situation, trigger, or workflow where the problem shows up. Context helps validate whether the problem is real and recurring.',
    fallbackSuggestions: ['During quarterly planning', 'When onboarding new customers', 'At handoff between teams', 'Under time pressure'],
    order: 2,
  },
  {
    sectionId: 'customers',
    question: 'Who experiences {ref:problem} most acutely?',
    hint: 'Be specific about the persona. Not just "managers" — what kind? In what industry? At what company stage? The more specific, the better.',
    fallbackSuggestions: ['Product managers', 'Operations leads', 'Small business owners', 'Field service teams'],
    order: 3,
  },
  {
    sectionId: 'emotional-impact',
    question: 'How does {ref:problem} make {ref:customers} feel?',
    hint: 'Emotional impact drives urgency. Frustrated? Anxious? Overwhelmed? Embarrassed? The stronger the emotion, the more willing they are to pay for a solution.',
    fallbackSuggestions: ['Frustrated by wasted effort', 'Anxious about missing deadlines', 'Overwhelmed by complexity', 'Embarrassed by errors'],
    order: 4,
  },
  {
    sectionId: 'quantifiable-impact',
    question: 'What is the measurable cost of {ref:problem}?',
    hint: 'Quantify in time, money, or other metrics. "Costs 10 hours/week" or "20% error rate" is more compelling than "it\'s expensive."',
    fallbackSuggestions: ['Hours lost per week', 'Revenue leaked per quarter', 'Error rate percentage', 'Customer churn attributed'],
    order: 5,
  },
  {
    sectionId: 'alternatives',
    question: 'What do {ref:customers} currently do to work around {ref:problem}?',
    hint: 'List the existing solutions, workarounds, or hacks. This reveals how people cope today and sets the bar your solution must clear.',
    fallbackSuggestions: ['Spreadsheets & email', 'Hire more people', 'Custom internal tool', 'Ignore it and accept the cost'],
    order: 6,
  },
  {
    sectionId: 'alternative-shortcomings',
    question: 'What\'s wrong with {ref:alternatives}?',
    hint: 'Why aren\'t current workarounds good enough? This is the gap your solution fills. Be specific about limitations.',
    fallbackSuggestions: ['Too time-consuming', 'Doesn\'t scale', 'Error-prone', 'Requires expertise few have'],
    order: 7,
  },
]

// ---------------------------------------------------------------------------
// Problem Statement Canvas — Challenge Rules
// ---------------------------------------------------------------------------

export const PSC_CHALLENGE_RULES: ChallengeRule[] = [
  {
    id: 'psc-no-problem',
    type: 'empty_section',
    severity: 'critical',
    title: 'Problem not defined',
    descriptionTemplate: "The most important section is empty. Without a clear problem statement, everything else is speculation.",
    affectedSection: 'problem',
  },
  {
    id: 'psc-no-customer',
    type: 'empty_section',
    severity: 'critical',
    title: 'Who has this problem?',
    descriptionTemplate: "You've described {ref:problem} but haven't identified who suffers from it. A problem without a customer isn't a market opportunity.",
    affectedSection: 'customers',
  },
  {
    id: 'psc-no-impact',
    type: 'empty_section',
    severity: 'strategic',
    title: 'Impact not quantified',
    descriptionTemplate: "Without measurable impact, it's hard to justify building a solution. How much does {ref:problem} cost {ref:customers} in time, money, or quality?",
    affectedSection: 'quantifiable-impact',
  },
  {
    id: 'psc-no-alternatives',
    type: 'empty_section',
    severity: 'strategic',
    title: 'No alternatives mapped',
    descriptionTemplate: "If {ref:customers} have no workarounds for {ref:problem}, either the problem isn't painful enough or you haven't looked hard enough.",
    affectedSection: 'alternatives',
  },
  {
    id: 'psc-no-shortcomings',
    type: 'empty_section',
    severity: 'opportunity',
    title: 'Alternative shortcomings missing',
    descriptionTemplate: "You've listed {count:alternatives} alternatives but haven't explained why they fall short. This is the gap that justifies your solution.",
    affectedSection: 'alternative-shortcomings',
  },
]

// ---------------------------------------------------------------------------
// Problem Statement Canvas — Story Prompt
// ---------------------------------------------------------------------------

export const PSC_STORY_PROMPT = `You are a problem discovery coach. Write a compelling problem narrative for {projectName}.

Canvas data:
{canvasData}

Rules:
- Write in present tense, active voice
- Start with who faces this problem and the context where it occurs
- Then describe the problem itself — make it vivid and relatable
- Include both the emotional toll and the measurable cost
- Mention what people do today to cope and why those workarounds fail
- End with why this problem deserves a better solution
- No bullet points. No headers. No markdown.
- Maximum 200 words
- If sections are empty, don't mention them - focus on what's known
- Be specific, not generic. Use the actual entity names.`

// ---------------------------------------------------------------------------
// Persona Canvas — Guided Questions
// ---------------------------------------------------------------------------

export const PERSONA_GUIDED_QUESTIONS: GuidedQuestion[] = [
  {
    sectionId: 'name-role',
    question: 'Who is this persona? Give them a name and role.',
    hint: 'Make them real. A specific name and job title makes the persona memorable and easier to design for. Example: "Maria, VP of Product at a Series B SaaS company."',
    fallbackSuggestions: ['Product Manager', 'Engineering Lead', 'Small Business Owner', 'Marketing Director'],
    order: 1,
  },
  {
    sectionId: 'headaches',
    question: 'What professional headaches does {ref:name-role} deal with regularly?',
    hint: 'Think about recurring frustrations in their work: things that waste time, cause stress, or prevent them from doing their best work.',
    fallbackSuggestions: ['Too many meetings', 'Lack of clear priorities', 'Data silos across teams', 'Constant context switching'],
    order: 2,
  },
  {
    sectionId: 'opportunities',
    question: 'What professional opportunities is {ref:name-role} excited about?',
    hint: 'What positive outcomes are they working toward? Promotions, new skills, team growth, process improvements?',
    fallbackSuggestions: ['Growing the team', 'Launching a new product', 'Improving team efficiency', 'Getting promoted'],
    order: 3,
  },
  {
    sectionId: 'need',
    question: 'What does {ref:name-role} really need at a fundamental level?',
    hint: 'Go beyond surface wants. What is their core need? Control? Recognition? Certainty? Growth? Belonging?',
    fallbackSuggestions: ['To feel in control', 'Recognition for impact', 'Certainty in decisions', 'More time for strategic work'],
    order: 4,
  },
  {
    sectionId: 'fears',
    question: 'What keeps {ref:name-role} up at night?',
    hint: 'Personal anxieties that drive behaviour: fear of failure, fear of missing out, fear of irrelevance, fear of making wrong bets.',
    fallbackSuggestions: ['Making the wrong strategic bet', 'Being replaced by AI', 'Missing market window', 'Team burnout'],
    order: 5,
  },
  {
    sectionId: 'hopes',
    question: 'What does {ref:name-role} hope their future looks like?',
    hint: 'Aspirations and goals — both personal and professional. What would success look like in 2-3 years?',
    fallbackSuggestions: ['Leading a high-performing team', 'Being known as an innovator', 'Work-life balance', 'Building something meaningful'],
    order: 6,
  },
  {
    sectionId: 'negative-trends',
    question: 'What negative trends in the environment worry {ref:name-role}?',
    hint: 'Market shifts, economic headwinds, industry disruption, regulatory changes — external forces that create pressure.',
    fallbackSuggestions: ['Economic uncertainty', 'AI disruption', 'Increasing competition', 'Tightening budgets'],
    order: 7,
  },
  {
    sectionId: 'positive-trends',
    question: 'What positive trends create tailwinds for {ref:name-role}?',
    hint: 'Technology advances, market growth, cultural shifts, new regulations — external forces that create opportunity.',
    fallbackSuggestions: ['Remote work normalisation', 'AI productivity tools', 'Growing market demand', 'Digital transformation'],
    order: 8,
  },
]

// ---------------------------------------------------------------------------
// Persona Canvas — Challenge Rules
// ---------------------------------------------------------------------------

export const PERSONA_CHALLENGE_RULES: ChallengeRule[] = [
  {
    id: 'persona-no-name',
    type: 'empty_section',
    severity: 'critical',
    title: 'Persona not named',
    descriptionTemplate: "A persona without a name and role is just an abstract segment. Give them identity to make them real and memorable.",
    affectedSection: 'name-role',
  },
  {
    id: 'persona-no-headaches',
    type: 'empty_section',
    severity: 'critical',
    title: 'No headaches identified',
    descriptionTemplate: "You've named {ref:name-role} but haven't mapped their pain points. Understanding frustrations is essential for building products they'll pay for.",
    affectedSection: 'headaches',
  },
  {
    id: 'persona-no-need',
    type: 'empty_section',
    severity: 'strategic',
    title: 'Core need missing',
    descriptionTemplate: "{ref:name-role} has headaches and hopes, but what do they fundamentally need? The core need is the anchor for your value proposition.",
    affectedSection: 'need',
  },
  {
    id: 'persona-surface-level',
    type: 'imbalance',
    severity: 'strategic',
    title: 'Surface-level persona',
    descriptionTemplate: "You have {count:headaches} headaches but only {count:fears} fears and {count:hopes} hopes. A complete persona includes emotional depth — fears and aspirations, not just job frustrations.",
    affectedSection: 'fears',
  },
  {
    id: 'persona-no-context',
    type: 'empty_section',
    severity: 'opportunity',
    title: 'Environment not mapped',
    descriptionTemplate: "Without trends, {ref:name-role} exists in a vacuum. External forces shape their priorities and urgency.",
    affectedSection: 'negative-trends',
  },
]

// ---------------------------------------------------------------------------
// Persona Canvas — Story Prompt
// ---------------------------------------------------------------------------

export const PERSONA_STORY_PROMPT = `You are a user researcher. Write a vivid persona narrative for {projectName}.

Canvas data:
{canvasData}

Rules:
- Write in present tense, third person
- Start with who they are — name, role, and what their day looks like
- Then describe their professional headaches and what they're trying to achieve
- Weave in their fears and hopes — make them human
- Include relevant trends shaping their world
- End with what they fundamentally need and why it matters
- No bullet points. No headers. No markdown.
- Maximum 200 words
- If sections are empty, don't mention them - focus on what's known
- Be specific, not generic. Use the actual entity names.
- Skip the photo section entirely.`

// ---------------------------------------------------------------------------
// Merge helpers
// ---------------------------------------------------------------------------

/**
 * Returns the full interactive config for a given template ID.
 * Currently only BMC has interactive config; others return undefined fields.
 */
export function getInteractiveConfig(templateId: string) {
  switch (templateId) {
    case 'business-model-canvas':
      return {
        guidedQuestions: BMC_GUIDED_QUESTIONS,
        challengeRules: BMC_CHALLENGE_RULES,
        relatedSections: BMC_RELATED_SECTIONS,
        storyPromptTemplate: BMC_STORY_PROMPT,
      }
    case 'value-proposition-canvas':
      return {
        guidedQuestions: VPC_GUIDED_QUESTIONS,
        challengeRules: VPC_CHALLENGE_RULES,
        relatedSections: undefined,
        storyPromptTemplate: VPC_STORY_PROMPT,
      }
    case 'positioning-canvas':
      return {
        guidedQuestions: POS_GUIDED_QUESTIONS,
        challengeRules: POS_CHALLENGE_RULES,
        relatedSections: undefined,
        storyPromptTemplate: POS_STORY_PROMPT,
      }
    case 'problem-statement-canvas':
      return {
        guidedQuestions: PSC_GUIDED_QUESTIONS,
        challengeRules: PSC_CHALLENGE_RULES,
        relatedSections: undefined,
        storyPromptTemplate: PSC_STORY_PROMPT,
      }
    case 'persona-canvas':
      return {
        guidedQuestions: PERSONA_GUIDED_QUESTIONS,
        challengeRules: PERSONA_CHALLENGE_RULES,
        relatedSections: undefined,
        storyPromptTemplate: PERSONA_STORY_PROMPT,
      }
    default:
      return {
        guidedQuestions: undefined,
        challengeRules: undefined,
        relatedSections: undefined,
        storyPromptTemplate: undefined,
      }
  }
}
